import { HStack } from "@/components/layout/HStack";
import { Badge } from "@/components/ui/Badge/Badge";
import { Button } from "@/components/ui/Button/Button";
import { DashboardCard } from "@/components/ui/DashboardCard/DashboardCard";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu/DropdownMenu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table/Table";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnFiltersState,
  type SortingState,
} from "@tanstack/react-table";
import { format } from "date-fns";
import {
  ArrowDownAZ,
  ArrowUpAZ,
  ArrowUpDown,
  CircleMinus,
  CirclePlus,
  Filter,
  Pencil,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { buildSignUpRows } from "../../utils/signUp/buildSignUpRows";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/Tooltip/Tooltip";
import { useSearchParams } from "react-router-dom";
import { parseCsv, writeCsv } from "@/utils/csv";

export type SignUpRow = {
  rotationId: string;
  rotationDate: string;
  editOpen: boolean;
  sessionId: string;
  sessionName: string;
  multiWeek: boolean;
  enrollmentId: string | undefined;
  preferencesId: string | undefined;
};

const getColumns = (
  onCreate: (rotationId: string, sessionId: string) => void,
  onEditSingle: (
    rotationId: string,
    sessionId: string,
    enrollmentId: string,
  ) => void,
  onEditMulti: (
    rotationId: string,
    sessionId: string,
    preferencesId: string,
  ) => void,
  onRemoveSingle: (
    rotationId: string,
    sessionId: string,
    enrollmentId: string,
  ) => void,
  onRemoveMulti: (
    rotationId: string,
    sessionId: string,
    preferencesId: string,
  ) => void,
): ColumnDef<SignUpRow>[] => [
  {
    accessorKey: "rotationDate",
    header: ({ column }) => (
      <Button
        variant="ghost"
        style={{
          fontWeight: "var(--font-medium)",
          height: "fit-content",
          padding: "var(--space-xxs) var(--space-xs)",
          marginLeft: "calc(var(--space-xs) * -1)",
        }}
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Date{" "}
        {column.getIsSorted() === "asc" ? (
          <ArrowDownAZ />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowUpAZ />
        ) : (
          <ArrowUpDown />
        )}
      </Button>
    ),
    cell: ({ row }) => format(row.original.rotationDate, "MMMM dd, yyyy"),
    filterFn: (row, columnId, filterValue) => {
      const selected = (filterValue as string[]) ?? [];
      if (selected.length === 0) return true;

      const v = row.getValue(columnId);
      const dt = v instanceof Date ? v : new Date(String(v));
      if (Number.isNaN(dt.getTime())) return false;

      return selected.includes(dt.toISOString());
    },
    sortingFn: "datetime",
  },
  {
    accessorKey: "sessionName",
    header: ({ column }) => (
      <Button
        variant="ghost"
        style={{
          fontWeight: "var(--font-medium)",
          height: "fit-content",
          padding: "var(--space-xxs) var(--space-xs)",
          marginLeft: "calc(var(--space-xs) * -1)",
        }}
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Session{" "}
        {column.getIsSorted() === "asc" ? (
          <ArrowDownAZ />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowUpAZ />
        ) : (
          <ArrowUpDown />
        )}
      </Button>
    ),
    filterFn: "arrIncludesSome",
  },
  {
    accessorKey: "multiWeek",
    header: ({ column }) => (
      <Button
        variant="ghost"
        style={{
          fontWeight: "var(--font-medium)",
          height: "fit-content",
          padding: "var(--space-xxs) var(--space-xs)",
          marginLeft: "calc(var(--space-xs) * -1)",
        }}
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Multi-Week{" "}
        {column.getIsSorted() === "asc" ? (
          <ArrowDownAZ />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowUpAZ />
        ) : (
          <ArrowUpDown />
        )}
      </Button>
    ),
    cell: ({ row }) => {
      return row.original.multiWeek === true ? (
        <Badge variant="success">Yes</Badge>
      ) : (
        <Badge variant="destructive">No</Badge>
      );
    },
    filterFn: (row, columnId, value: ("yes" | "no")[] | undefined) => {
      if (!value || value.length === 0) return true;
      const v = row.getValue(columnId) as boolean;
      return value.includes(v ? "yes" : "no");
    },
  },
  {
    id: "actions",
    header: () => (
      <div style={{ textAlign: "right", fontWeight: "var(--font-medium)" }}>
        Actions
      </div>
    ),
    cell: ({ row }) => (
      <HStack justify="flex-end">
        {row.original.enrollmentId ? (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon-small"
                  onClick={() =>
                    onEditSingle(
                      row.original.rotationId,
                      row.original.sessionId,
                      row.original.enrollmentId!,
                    )
                  }
                  disabled={!row.original.editOpen}
                >
                  <Pencil />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                Edit enrollment for {row.original.sessionName} on{" "}
                {format(row.original.rotationDate, "MMMM dd, yyyy")}
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon-small"
                  variant="destructive"
                  onClick={() =>
                    onRemoveSingle(
                      row.original.rotationId,
                      row.original.sessionId,
                      row.original.enrollmentId!,
                    )
                  }
                  disabled={!row.original.editOpen}
                >
                  <CircleMinus />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                Remove enrollment for {row.original.sessionName} on{" "}
                {format(row.original.rotationDate, "MMMM dd, yyyy")}
              </TooltipContent>
            </Tooltip>
          </>
        ) : row.original.preferencesId ? (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon-small"
                  onClick={() =>
                    onEditMulti(
                      row.original.rotationId,
                      row.original.sessionId,
                      row.original.preferencesId!,
                    )
                  }
                  disabled={!row.original.editOpen}
                >
                  <Pencil />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                Edit preferences for {row.original.sessionName} on{" "}
                {format(row.original.rotationDate, "MMMM dd, yyyy")}
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon-small"
                  variant="destructive"
                  onClick={() =>
                    onRemoveMulti(
                      row.original.rotationId,
                      row.original.sessionId,
                      row.original.preferencesId!,
                    )
                  }
                  disabled={!row.original.editOpen}
                >
                  <CircleMinus />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                Remove preferences for {row.original.sessionName} on{" "}
                {format(row.original.rotationDate, "MMMM dd, yyyy")}
              </TooltipContent>
            </Tooltip>
          </>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon-small"
                onClick={() =>
                  onCreate(row.original.rotationId, row.original.sessionId)
                }
                disabled={!row.original.editOpen}
              >
                <CirclePlus />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              Sign-up for {row.original.sessionName} on{" "}
              {format(row.original.rotationDate, "MMMM dd, yyyy")}
            </TooltipContent>
          </Tooltip>
        )}
      </HStack>
    ),
  },
];

const DataTable = ({
  data,
  columns,
}: {
  data: SignUpRow[];
  columns: ColumnDef<SignUpRow>[];
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  // Sorting
  const sortKRaw = searchParams.get("sortK");
  const sortVRaw = searchParams.get("sortV");

  const urlSorting = useMemo<SortingState>(() => {
    const validK =
      sortKRaw === "rotationDate" ||
      sortKRaw === "sessionName" ||
      sortKRaw === "multiWeek";

    const validV = sortVRaw === "asc" || sortVRaw === "desc";

    if (!validK || !validV) return [];
    return [{ id: sortKRaw, desc: sortVRaw === "desc" }];
  }, [sortKRaw, sortVRaw]);

  const onSortingChange = (
    updater: SortingState | ((old: SortingState) => SortingState),
  ) => {
    const current = urlSorting;
    const next = typeof updater === "function" ? updater(current) : updater;

    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);

      const first = next[0];
      if (!first) {
        sp.delete("sortK");
        sp.delete("sortV");
        return sp;
      }

      sp.set("sortK", first.id);
      sp.set("sortV", first.desc ? "desc" : "asc");
      return sp;
    });
  };

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting: urlSorting,
      columnFilters,
    },
  });

  // -----------------------
  // URL-backed filters
  // -----------------------

  // Rotation Dates (rot)
  const urlRot = useMemo(
    () => parseCsv(searchParams.get("rot")),
    [searchParams],
  );

  // Sessions (sess)
  const urlSesh = useMemo(
    () => parseCsv(searchParams.get("sesh")),
    [searchParams],
  );

  // Multi-week (mw)
  type MultiWeekFilter = "yes" | "no";
  const urlMw = useMemo(
    () => parseCsv(searchParams.get("mw")) as MultiWeekFilter[],
    [searchParams],
  );

  // Apply URL -> table filters
  useEffect(() => {
    table.getColumn("rotationDate")?.setFilterValue(urlRot);
    table.getColumn("sessionName")?.setFilterValue(urlSesh);
    table.getColumn("multiWeek")?.setFilterValue(urlMw);
  }, [table, urlRot, urlSesh, urlMw]);

  const rotations = useMemo(() => {
    const map = new Map<string, Date>();
    for (const d of data) {
      const dt = new Date(d.rotationDate);
      if (Number.isNaN(dt.getTime())) continue;
      const iso = dt.toISOString();
      if (!map.has(iso)) map.set(iso, dt);
    }
    return Array.from(map.values()).sort((a, b) => a.getTime() - b.getTime());
  }, [data]);

  const toggleRot = (dt: Date) => {
    const iso = dt.toISOString();
    const next = urlRot.includes(iso)
      ? urlRot.filter((x) => x !== iso)
      : [...urlRot, iso];

    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      writeCsv(sp, "rot", next);
      return sp;
    });
  };

  const clearRotFilter = () => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      sp.delete("rot");
      return sp;
    });
  };

  // Sessions options
  const sessions = useMemo(() => {
    const set = new Set<string>();
    for (const d of data) if (d.sessionName) set.add(d.sessionName);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [data]);

  const toggleSesh = (sesh: string) => {
    const next = urlSesh.includes(sesh)
      ? urlSesh.filter((x) => x !== sesh)
      : [...urlSesh, sesh];

    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      writeCsv(sp, "sesh", next);
      return sp;
    });
  };

  const clearSeshFilter = () => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      sp.delete("sesh");
      return sp;
    });
  };

  // Multi-week options
  const multiWeekOptions: MultiWeekFilter[] = ["yes", "no"];

  const availableMultiWeek = useMemo(() => {
    const set = new Set<MultiWeekFilter>();
    for (const d of data) set.add(d.multiWeek ? "yes" : "no");
    return multiWeekOptions.filter((v) => set.has(v));
  }, [data]);

  const toggleMultiWeek = (v: MultiWeekFilter) => {
    const next = urlMw.includes(v)
      ? urlMw.filter((x) => x !== v)
      : [...urlMw, v];

    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      writeCsv(sp, "mw", next);
      return sp;
    });
  };

  const clearMultiWeekFilter = () => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      sp.delete("mw");
      return sp;
    });
  };

  const totalCount = table.getCoreRowModel().rows.length;
  const filteredCount = table.getFilteredRowModel().rows.length;

  return (
    <>
      <HStack align="center">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              style={{
                height: "fit-content",
                padding: "var(--space-xxs) var(--space-xs)",
              }}
            >
              <Filter /> Filter
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>Dates</DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {rotations.map((r) => {
                  const iso = r.toISOString();
                  return (
                    <DropdownMenuCheckboxItem
                      key={iso}
                      checked={urlRot.includes(iso)}
                      onCheckedChange={() => toggleRot(r)}
                      onSelect={(e) => e.preventDefault()}
                    >
                      {format(r, "MMMM dd, yyyy")}
                    </DropdownMenuCheckboxItem>
                  );
                })}

                <DropdownMenuSeparator />

                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    clearRotFilter();
                  }}
                  disabled={urlRot.length === 0}
                >
                  Clear
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>Sessions</DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {sessions.map((s) => (
                  <DropdownMenuCheckboxItem
                    key={s}
                    checked={urlSesh.includes(s)}
                    onCheckedChange={() => toggleSesh(s)}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {s}
                  </DropdownMenuCheckboxItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    clearSeshFilter();
                  }}
                  disabled={urlSesh.length === 0}
                >
                  Clear
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>Multi-Week</DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {availableMultiWeek.map((a) => (
                  <DropdownMenuCheckboxItem
                    key={a}
                    checked={urlMw.includes(a)}
                    onCheckedChange={() => toggleMultiWeek(a)}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {a === "yes" ? "Yes" : "No"}
                  </DropdownMenuCheckboxItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    clearMultiWeekFilter();
                  }}
                  disabled={urlMw.length === 0}
                >
                  Clear
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </DropdownMenuContent>
        </DropdownMenu>
        <p
          style={{
            color: "var(--muted-foreground)",
          }}
        >
          {filteredCount} of {totalCount} sessions
        </p>
      </HStack>
      <Table borderColor="var(--secondary-border)">
        <TableHeader>
          {table.getHeaderGroups().map((h) => (
            <TableRow key={h.id}>
              {h.headers.map((header) => (
                <TableHead key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length}>No records</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </>
  );
};

const SignUpDataTable = ({
  data,
  onCreate,
  onEditSingle,
  onEditMulti,
  onRemoveSingle,
  onRemoveMulti,
}: {
  data: {
    rotations: any[];
    sessions: any[];
    enrollments: any[];
    preferences: any[];
  };
  onCreate: (rotationId: string, sessionId: string) => void;
  onEditSingle: (
    rotationId: string,
    sessionId: string,
    enrollmentId: string,
  ) => void;
  onEditMulti: (
    rotationId: string,
    sessionId: string,
    preferencesId: string,
  ) => void;
  onRemoveSingle: (
    rotationId: string,
    sessionId: string,
    enrollmentId: string,
  ) => void;
  onRemoveMulti: (
    rotationId: string,
    sessionId: string,
    preferencesId: string,
  ) => void;
}) => {
  const rows = useMemo(() => buildSignUpRows(data), [data]);
  const columns = useMemo(
    () =>
      getColumns(
        onCreate,
        onEditSingle,
        onEditMulti,
        onRemoveSingle,
        onRemoveMulti,
      ),
    [onCreate, onEditSingle, onEditMulti, onRemoveSingle, onRemoveMulti],
  );

  return (
    <DashboardCard>
      <DataTable data={rows} columns={columns} />
    </DashboardCard>
  );
};

export { SignUpDataTable };
